import { removeNullParams, snakeCaseObject } from '@/helper/formatter'
import { requests, responseBody, instance } from '../http/axios'
import {
  ATTRIBUTION_SOURCE,
  CalendarEvent,
  Contact,
  CreateOrderRequest,
  ExperienceOption,
  MasterOrder,
  Order,
  Paginator,
} from '@/types'
import { format } from 'date-fns'
import { pick } from 'lodash-es'
import type { MarkAsPaidMethod } from '@/lib/payment-methods'

export type OrderFilters = {
  limit?: number
  page?: number
  q?: string
  status?: string
  dateBy?: string
  from?: string
  until?: string
  organizerId?: string
  createdById?: string
  sellerId?: string
  chainId?: string
  locationIds?: string
  internalLabelIds?: string
  guestType?: string
  /**
   * Voucher source filter (Plan 01-08, INFOPT-03). Accepts the values
   * exported from `@/types/voucher` as `VOUCHER_SOURCE_FILTER_VALUES`:
   * `'all' | 'internal' | 'external_api'`. Omit or set to `'all'` for no filter.
   */
  source?: string
  searchBy?: string
  orderIds?: number[] | string
  orderBy?: string
  orderDirection?: string
  // DEV-370: filtra per tipo di rivendita (direct | group | external)
  resellType?: string
  type?: 'excel' | 'printable_html'
}

// DEV-712 — slot-level bulk action results. Identifiers are order uids, matching
// what the calendar rows carry.
export type BulkSlotCancelResult = { canceled: string[]; skipped: string[] }
export type BulkSlotRescheduleResult = {
  rescheduled: string[]
  failed: { uid: string; reason: string }[]
}

export const OrderService = {
  getOrders: async (params: OrderFilters): Promise<Paginator<Order>> =>
    await requests.get(`orders?` + removeNullParams(params).toString()),

  getOrdersByIds: async (params: OrderFilters): Promise<Paginator<Order>> =>
    await requests.get(
      `get_orders_by_ids?` + removeNullParams(params).toString()
    ),

  getValidatedOrders: async (params: OrderFilters): Promise<Paginator<Order>> =>
    await requests.get(
      `validated_orders?` + removeNullParams(params).toString()
    ),

  getOrder: async (id: string | number): Promise<Order> =>
    await requests.get(`orders/${id}`).then(responseBody),

  getMasterOrder: async (
    idempotency: string,
    locale?: string
  ): Promise<MasterOrder> =>
    await requests
      .get(`master_orders/${idempotency}${locale ? `?locale=${locale}` : ''}`)
      .then(responseBody),

  createOrder: async (
    idempotency: string,
    orderRequest: CreateOrderRequest
  ): Promise<MasterOrder> =>
    await requests
      .post(`orders`, {
        ...orderRequest,
        selectedDate: format(orderRequest.selectedDate!, 'yyyy-MM-dd'),
        idempotency,
      })
      .then(responseBody),

  markMasterOrderAsExpired: async (id: string): Promise<void> =>
    await requests.put(`master_orders/${id}/mark_as_expired`, {}),

  removeOrder: async (idempotency: string, id: number): Promise<void> =>
    await requests.delete(`orders/${id}?idempotency=${idempotency}`),

  updateContacts: async (
    id: string,
    contact: Contact,
    source: string,
    jwtToken?: string
  ): Promise<void> => {
    // Defensive guard (DEV-796): a null contact would throw on destructure.
    // An admin order placed without a customer legitimately has no contact to
    // update (traced via DEV-830: create-order checkout with no customer), so
    // skip silently. Callers now guard, this just backstops the destructure.
    if (!contact) {
      return
    }
    const {
      customAnswers,
      marketingConsent,
      recommendExperiences,
      specialCategoriesConsent,
      // DEV-728 — top-level association, not part of the contact fields.
      customerId,
      ...contactData
    } = contact
    const payload: any = {
      contacts: [snakeCaseObject(contactData)],
      source,
    }

    // DEV-728 — associate the existing guest the operator picked in the search
    // (or a resolved PMS mirror), instead of auto-creating from the contact.
    if (customerId != null) {
      payload.customer_id = customerId
    }

    // Add custom answers if present
    if (customAnswers) {
      payload.custom_answers = customAnswers
    }

    // Add marketing consent if present
    if (marketingConsent !== undefined) {
      payload.marketing_consent = marketingConsent
    }

    // Add recommend experiences if present
    if (recommendExperiences !== undefined) {
      payload.recommend_experiences = recommendExperiences
    }

    // Add special-categories (Art. 9 GDPR) consent if present
    if (specialCategoriesConsent !== undefined) {
      payload.special_categories_consent = specialCategoriesConsent
    }

    const config = jwtToken
      ? { headers: { Authorization: `Bearer ${jwtToken}` } }
      : undefined

    return await requests.put(
      `master_orders/${id}/update_customer`,
      payload,
      config
    )
  },

  placeMasterOrder: async (masterOrder: MasterOrder): Promise<MasterOrder> =>
    await requests
      .put(
        `master_orders/${masterOrder.id}/place_order`,
        snakeCaseObject(
          pick(masterOrder, [
            'locale',
            'paymentMethod',
            'additionalNotes',
            'finalAmount',
          ])
        )
      )
      .then(responseBody),

  blockBooking: async (params: {
    experienceId: number
    selectedDate: string
    startsSlot: string
    sellerId: number
    optionId: string
    selectedPrices: Record<number | string, number>
  }): Promise<void> =>
    await requests.post(`experiences/${params.experienceId}/block_booking`, {
      selectedDate: params.selectedDate,
      startsSlot: params.startsSlot,
      sellerId: params.sellerId,
      optionId: params.optionId,
      selectedPrices: params.selectedPrices,
    }),

  refresh: async (masterOrder: MasterOrder): Promise<MasterOrder> =>
    await requests.put(`master_orders/${masterOrder.id}/refresh`, {}),

  getOrCreateClientSecret: async (
    id: string
  ): Promise<{
    clientSecret: string | null
    alreadyPaid: boolean
    // Publishable key of the account the PaymentIntent was created on
    // (stripeChargeCompany). Must be used to init Stripe.js, not the seller's
    // key, or Elements cannot load the secret for a resold cart (DEV-821).
    stripePublicKey: string | null
  }> =>
    await requests.post(`master_orders/${id}/client_secret`, {}),

  markFreeOrderAsPaid: async (id: string): Promise<void> =>
    await requests.post(`master_orders/${id}/complete-free-order`, {}),

  markAsPaid: async (
    id: number,
    paymentMethod: MarkAsPaidMethod,
    finalAmount?: number
  ): Promise<void> =>
    await requests.put(`orders/${id}/mark_as_paid`, {
      payment_method: paymentMethod,
      ...(finalAmount !== undefined && { final_amount: finalAmount }),
    }),

  markAsCanceled: async (id: number): Promise<void> =>
    await requests.put(`orders/${id}/mark_as_canceled`, {}),

  markAsCompleted: async (id: number): Promise<void> =>
    await requests.put(`orders/${id}/mark_as_completed`, {}),

  markAsConfirmed: async (id: number): Promise<void> =>
    await requests.put(`orders/${id}/mark_as_confirmed`, {}),

  rescheduleOrder: async (
    orderUid: string,
    selectedDate: string,
    startsSlot: string,
    componentSelections?: {
      component_option_id: number
      date: string
      slot: string
    }[],
    // DEV-913: move the order onto another option with identical rates.
    experienceOptionId?: number
  ): Promise<Order> =>
    await requests
      .put(`orders/${orderUid}/reschedule`, {
        selected_date: selectedDate,
        starts_slot: startsSlot,
        ...(componentSelections && { component_selections: componentSelections }),
        ...(experienceOptionId && { experience_option_id: experienceOptionId }),
      })
      .then(responseBody),

  // DEV-913: options open on `date` the order can be rescheduled onto: its
  // own option plus those with identical rates. `book_now` bypasses the
  // cutoff, as for every operator reschedule (DEV-730).
  getRescheduleOptions: async (
    orderUid: string,
    date: string,
    locale?: string
  ): Promise<ExperienceOption[]> =>
    await requests
      .get(
        `orders/${orderUid}/reschedule_options/${date}?book_now=1${
          locale ? `&locale=${locale}` : ''
        }`
      )
      .then(responseBody),

  // DEV-712 — cancel every booking of a slot at once. Returns the uids
  // cancelled and the ones skipped (already cancelled).
  bulkCancelOrders: async (uids: string[]): Promise<BulkSlotCancelResult> =>
    await requests.post('orders/bulk-cancel', { uids }),

  // DEV-712 — reschedule every booking of a slot to a new date/time. Best
  // effort: `failed` lists the uids that could not move (e.g. target slot full).
  bulkRescheduleOrders: async (
    uids: string[],
    selectedDate: string,
    startsSlot: string,
    experienceOptionId?: number
  ): Promise<BulkSlotRescheduleResult> =>
    await requests.post('orders/bulk-reschedule', {
      uids,
      selected_date: selectedDate,
      starts_slot: startsSlot,
      ...(experienceOptionId && { experience_option_id: experienceOptionId }),
    }),

  completeReservation: async (reservationId: number): Promise<void> =>
    await requests.put(`reservations/${reservationId}/complete`, {}),

  updateReservationInventory: async (
    reservationId: number,
    sku: string
  ): Promise<void> =>
    await requests.put(`reservations/${reservationId}/inventory`, { sku }),

  // DEV-810: combo (round-trip) tickets validate one leg at a time; pass the
  // leg's component option id, omit it for whole-order validation.
  validateOrder: async (
    orderId: string,
    componentOptionId?: number
  ): Promise<Order> =>
    await requests.post(
      `orders/${orderId}/validate`,
      componentOptionId != null ? { component_option_id: componentOptionId } : {}
    ),

  getCalendarEvents: async (
    startsAt: string,
    endsAt: string,
    chainId?: string,
    locationIds?: string,
    experienceIds?: string,
    categoryIds?: string,
    tagIds?: string,
    labelIds?: string
  ): Promise<CalendarEvent[]> => {
    const params = new URLSearchParams()
    if (chainId) params.append('chainId', chainId)
    if (locationIds) params.append('locationIds', locationIds)
    if (experienceIds) params.append('experienceIds', experienceIds)
    if (categoryIds) params.append('categoryIds', categoryIds)
    if (tagIds) params.append('tagIds', tagIds)
    if (labelIds) params.append('labelIds', labelIds)
    const queryString = params.toString()
    return await requests
      .get(
        `order_events/${startsAt}/${endsAt}${
          queryString ? `?${queryString}` : ''
        }`
      )
      .then(responseBody)
  },

  verifyVoucher: async (code: string): Promise<Order> =>
    await requests.post(`vouchers/verify`, { code }).then(responseBody),

  updateGuestType: async (
    orderUid: string,
    guestType: string,
    attributionSource: string = ATTRIBUTION_SOURCE.SelfDeclared
  ): Promise<void> =>
    await requests.patch(`app/orders/${orderUid}/guest-type`, {
      guest_type: guestType,
      attribution_source: attributionSource,
    }),

  exportOrders: async (params: OrderFilters): Promise<void> => {
    const url = `orders/export?` + removeNullParams(params).toString()
    const response = await instance.get(url, { responseType: 'blob' })
    const blob = new Blob([response.data], {
      type: response.headers['content-type'],
    })
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl

    const timestamp = new Date().toISOString()
    const extension = params.type === 'printable_html' ? 'html' : 'xlsx'
    link.download = `bookings_export_${timestamp}.${extension}`

    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(downloadUrl)
  },
}
